import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs) {
  return twMerge(clsx(inputs))
}

export function formatAltitude(altitude) {
  if (!altitude && altitude !== 0) return '---'
  if (altitude >= 1000) {
    return `${(altitude / 1000).toFixed(1)}k ft`
  }
  return `${Math.round(altitude)} ft`
}

export function formatSpeed(speed) {
  if (!speed) return '---'
  return `${Math.round(speed)} mph`
}

export function formatHeading(heading) {
  if (!heading && heading !== 0) return '---'
  const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW']
  const index = Math.round(heading / 45) % 8
  return `${Math.round(heading)}° ${directions[index]}`
}

export function formatVerticalRate(vr) {
  if (!vr) return 'level'
  if (vr > 0) return `+${Math.round(vr)} fpm`
  return `${Math.round(vr)} fpm`
}

export function formatCurrency(amount, currency = 'GBP') {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: currency,
  }).format(amount / 100)
}

export function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

export function formatTimeAgo(dateString) {
  const iso = dateString.includes('T') ? dateString : dateString.replace(' ', 'T') + 'Z'
  const seconds = Math.floor((Date.now() - new Date(iso)) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
  const days = Math.floor(hours / 24)
  return `${days} day${days === 1 ? '' : 's'} ago`
}

// icao: 3-letter callsign prefix (e.g. "BAW" in "BAW123"), iata: 2-letter code used
// for the logo file in public/airlines/{iata}.png
const AIRLINES = [
  { icao: 'BAW', iata: 'BA', name: 'British Airways' },
  { icao: 'AAL', iata: 'AA', name: 'American Airlines' },
  { icao: 'UAL', iata: 'UA', name: 'United Airlines' },
  { icao: 'DLH', iata: 'LH', name: 'Lufthansa' },
  { icao: 'AFR', iata: 'AF', name: 'Air France' },
  { icao: 'KLM', iata: 'KL', name: 'KLM Royal Dutch' },
  { icao: 'UAE', iata: 'EK', name: 'Emirates' },
  { icao: 'QTR', iata: 'QR', name: 'Qatar Airways' },
  { icao: 'CPA', iata: 'CX', name: 'Cathay Pacific' },
  { icao: 'JAL', iata: 'JL', name: 'Japan Airlines' },
  { icao: 'ANA', iata: 'NH', name: 'All Nippon Airways' },
  { icao: 'SIA', iata: 'SQ', name: 'Singapore Airlines' },
  { icao: 'VIR', iata: 'VS', name: 'Virgin Atlantic' },
  { icao: 'EZY', iata: 'U2', name: 'easyJet' },
  { icao: 'RYR', iata: 'FR', name: 'Ryanair' },
  { icao: 'SWA', iata: 'WN', name: 'Southwest' },
  { icao: 'DAL', iata: 'DL', name: 'Delta' },
  { icao: 'WZZ', iata: 'W6', name: 'Wizz Air' },
  { icao: 'EIN', iata: 'EI', name: 'Aer Lingus' },
  { icao: 'ICE', iata: 'FI', name: 'Icelandair' },
  { icao: 'SAS', iata: 'SK', name: 'Scandinavian Airlines' },
  { icao: 'FIN', iata: 'AY', name: 'Finnair' },
  { icao: 'TAP', iata: 'TP', name: 'TAP Air Portugal' },
  { icao: 'IBE', iata: 'IB', name: 'Iberia' },
  { icao: 'AZA', iata: 'AZ', name: 'Alitalia' },
  { icao: 'AUA', iata: 'OS', name: 'Austrian Airlines' },
  { icao: 'SWR', iata: 'LX', name: 'Swiss International' },
]

const AIRLINES_BY_CODE = {}
for (const a of AIRLINES) {
  AIRLINES_BY_CODE[a.icao] = a
  AIRLINES_BY_CODE[a.iata] = a
}

export function getAirlineFromCallsign(callsign) {
  if (!callsign) return { name: 'Unknown', code: '---', iata: null }
  const trimmed = callsign.trim().toUpperCase()
  const match = AIRLINES_BY_CODE[trimmed.substring(0, 3)] || AIRLINES_BY_CODE[trimmed.substring(0, 2)]
  if (match) return { name: match.name, code: match.icao, iata: match.iata }
  const prefix = trimmed.substring(0, 3)
  return { name: prefix, code: prefix, iata: null }
}

export function getAirlineLogoUrl(iata) {
  return iata ? `/airlines/${iata}.png` : null
}

